/**
 * Préparation des images avant envoi : réduites à 2000 px au plus sur le grand côté et
 * converties en WebP, pour rester dans le stockage gratuit (spécification §1 : 0 €).
 */

const COTE_MAXIMUM = 2000;
const QUALITE = 0.85;
/** Taille au-delà de laquelle une image est refusée (limite du seau `images`). */
export const TAILLE_MAXIMUM = 5 * 1024 * 1024;
/** Formats acceptés par le seau `images` (supabase/migrations). */
const FORMATS = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/avif'];
const TROP_LOURDE = 'Image trop lourde : 5 Mo au plus.';

export async function preparerImage(fichier: File): Promise<{ image: Blob; extension: string }> {
  if (!fichier.type.startsWith('image/')) throw new Error('Ce fichier n’est pas une image.');
  // Un GIF (peut-être animé) garde son format.
  if (fichier.type === 'image/gif') {
    if (fichier.size > TAILLE_MAXIMUM) throw new Error(TROP_LOURDE);
    return { image: fichier, extension: 'gif' };
  }
  try {
    const bitmap = await createImageBitmap(fichier);
    const echelle = Math.min(1, COTE_MAXIMUM / Math.max(bitmap.width, bitmap.height));
    const largeur = Math.round(bitmap.width * echelle);
    const hauteur = Math.round(bitmap.height * echelle);
    const canevas = document.createElement('canvas');
    canevas.width = largeur;
    canevas.height = hauteur;
    canevas.getContext('2d')!.drawImage(bitmap, 0, 0, largeur, hauteur);
    bitmap.close();
    const image = await new Promise<Blob | null>((resoudre) =>
      canevas.toBlob(resoudre, 'image/webp', QUALITE),
    );
    if (image && image.type === 'image/webp' && image.size < fichier.size * 1.2) {
      if (image.size > TAILLE_MAXIMUM) throw new Error(TROP_LOURDE);
      return { image, extension: 'webp' };
    }
  } catch (e) {
    if (e instanceof Error && e.message === TROP_LOURDE) throw e;
    // Format que le navigateur ne sait pas décoder : on garde le fichier d'origine.
  }
  if (!FORMATS.includes(fichier.type))
    throw new Error('Format non pris en charge : PNG, JPEG, WebP, GIF ou AVIF.');
  if (fichier.size > TAILLE_MAXIMUM) throw new Error(TROP_LOURDE);
  const extension = fichier.name.split('.').pop()?.toLowerCase() ?? 'png';
  return { image: fichier, extension: /^[a-z0-9]{2,5}$/.test(extension) ? extension : 'png' };
}

/** Dimensions naturelles d'une image (masquage d'image). */
export function dimensions(src: string): Promise<{ largeur: number; hauteur: number }> {
  return new Promise((resoudre, rejeter) => {
    const image = new Image();
    image.onload = () => resoudre({ largeur: image.naturalWidth, hauteur: image.naturalHeight });
    image.onerror = () => rejeter(new Error('Image illisible.'));
    image.src = src;
  });
}
